import {useState} from 'react';
import {TFunction} from 'i18next';
import {NotificationItem} from '../types';
import {ReceiveMailType, RECEIVE_MAIL_TYPE} from '../constants/alertSettingsConfig';

interface UseNotificationManagementProps {
  t: TFunction;
  enqueueSnackbar: any;
  emails: any[];
  updateValidationError: (field: string | any, error: string | undefined) => void;
}

/**
 * 通知先管理フック
 * 通知先リストの管理、ダイアログ制御、インライン/バッチ操作を提供
 */
export const useNotificationManagement = ({
  t,
  enqueueSnackbar,
  emails,
  updateValidationError
}: UseNotificationManagementProps) => {
  // 通知先リスト
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  // ダイアログ状態
  const [notificationListDialogOpen, setNotificationListDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  // 新規追加用の状態
  const [newEmail, setNewEmail] = useState<string>('');
  const [newReceiveMailType, setNewReceiveMailType] = useState<ReceiveMailType>(
    RECEIVE_MAIL_TYPE.ALL
  );

  // バッチ操作用の選択状態
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);

  /**
   * 通知先リストダイアログを開く
   */
  const handleOpenNotificationListDialog = () => {
    setNotificationListDialogOpen(true);
  };

  /**
   * 通知先リストダイアログを閉じる
   */
  const handleCloseNotificationListDialog = () => {
    setNotificationListDialogOpen(false);
  };

  /**
   * 通知先リストを更新
   */
  const handleNotificationsChange = (newNotifications: NotificationItem[]) => {
    setNotifications(newNotifications);
  };

  /**
   * 追加ダイアログを開く
   */
  const handleOpenAddDialog = () => {
    setNewEmail('');
    setNewReceiveMailType(RECEIVE_MAIL_TYPE.ALL);
    setAddDialogOpen(true);
  };

  /**
   * 追加ダイアログを閉じる
   */
  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
  };

  /**
   * 個別の送信設定を変更
   */
  const handleInlineSendSettingChange = (index: number, receiveMailType: ReceiveMailType) => {
    const updated = notifications.map((n, i) => (i === index ? {...n, receiveMailType} : n));
    setNotifications(updated);
  };

  /**
   * 個別の通知先を削除
   */
  const handleInlineDelete = (index: number) => {
    // 確認ダイアログを表示
    if (!window.confirm(t('この通知先を削除してもよろしいですか？'))) {
      return;
    }

    const updated = notifications.filter((_, i) => i !== index);
    setNotifications(updated);
    // 選択状態もクリア
    setSelectedIndices(selectedIndices.filter(i => i !== index).map(i => (i > index ? i - 1 : i)));
    enqueueSnackbar(t('通知先を削除しました'), {variant: 'success'});
  };

  /**
   * インラインで通知先を追加
   */
  const handleInlineAdd = () => {
    // バリデーション
    if (!newEmail) {
      const error = t('メールアドレスを選択してください');
      updateValidationError('newEmail', error);
      enqueueSnackbar(error, {variant: 'error'});
      return;
    }

    const emailData = emails.find(e => e.email === newEmail);
    if (!emailData) {
      const error = t('メールアドレスが存在しません');
      updateValidationError('newEmail', error);
      enqueueSnackbar(error, {variant: 'error'});
      return;
    }

    // 重複チェック
    if (notifications.some(n => n.email === emailData.id)) {
      const error = t('既に登録されている通知先です');
      updateValidationError('newEmail', error);
      enqueueSnackbar(error, {variant: 'error'});
      return;
    }

    // エラーをクリア
    updateValidationError('newEmail', undefined);

    // 追加
    const updated = [...notifications, {email: emailData.id, receiveMailType: newReceiveMailType}];
    setNotifications(updated);
    enqueueSnackbar(t('通知先を追加しました'), {variant: 'success'});

    // ダイアログを閉じる
    handleCloseAddDialog();
  };

  /**
   * 個別選択のトグル
   */
  const handleToggleSelect = (index: number) => {
    if (selectedIndices.includes(index)) {
      setSelectedIndices(selectedIndices.filter(i => i !== index));
    } else {
      setSelectedIndices([...selectedIndices, index]);
    }
  };

  /**
   * 全選択/全解除
   */
  const handleToggleSelectAll = () => {
    if (selectedIndices.length === notifications.length) {
      setSelectedIndices([]);
    } else {
      setSelectedIndices(notifications.map((_, i) => i));
    }
  };

  /**
   * バッチ削除
   */
  const handleBatchDelete = () => {
    if (selectedIndices.length === 0) return;

    // 確認ダイアログを表示
    if (
      !window.confirm(
        t('選択した{count}件の通知先を削除してもよろしいですか？', {count: selectedIndices.length})
      )
    ) {
      return;
    }

    const updated = notifications.filter((_, i) => !selectedIndices.includes(i));
    setNotifications(updated);
    setSelectedIndices([]);
    enqueueSnackbar(t('選択した通知先を削除しました'), {variant: 'success'});
  };

  /**
   * バッチ送信設定変更
   */
  const handleBatchSendSettingChange = (receiveMailType: ReceiveMailType) => {
    if (selectedIndices.length === 0) return;

    const updated = notifications.map((n, i) =>
      selectedIndices.includes(i) ? {...n, receiveMailType} : n
    );
    setNotifications(updated);
    enqueueSnackbar(t('選択した通知先の送信設定を変更しました'), {variant: 'success'});
  };

  /**
   * 通知先関連の状態をリセット
   */
  const resetNotifications = () => {
    setNotifications([]);
    setSelectedIndices([]);
    setNewEmail('');
    setNewReceiveMailType(RECEIVE_MAIL_TYPE.ALL);
  };

  return {
    // 通知先リスト
    notifications,
    setNotifications,

    // ダイアログ状態
    notificationListDialogOpen,
    handleOpenNotificationListDialog,
    handleCloseNotificationListDialog,
    handleNotificationsChange,

    // 追加ダイアログ
    addDialogOpen,
    handleOpenAddDialog,
    handleCloseAddDialog,

    // 新規追加用の状態
    newEmail,
    setNewEmail,
    newReceiveMailType,
    setNewReceiveMailType,

    // インライン操作
    handleInlineSendSettingChange,
    handleInlineDelete,
    handleInlineAdd,

    // バッチ操作
    selectedIndices,
    handleToggleSelect,
    handleToggleSelectAll,
    handleBatchDelete,
    handleBatchSendSettingChange,

    // リセット
    resetNotifications
  };
};
